import React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "underline-offset-4 hover:underline text-primary",

        // Harvey Norman Custom
        standard: "w-full border border-slate-700 justify-start hover:bg-slate-700 cursor-pointer",
        nav: "w-full justify-start hover:bg-slate-700 hover:text-accent-foreground cursor-pointer ",
        // Harvey Norman Red Gradient CTA (matches homepage grid)
        red: "w-full md:w-auto bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white px-8 py-6 rounded rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300",
        white: "w-full md:w-auto bg-white border border-red-700 text-red-700 font-semibold px-8 py-6 rounded rounded-lg hover:text-white hover:bg-gradient-to-r hover:from-red-600 hover:to-red-700 hover:border-transparent hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300",


      },
      size: {
        default: "h-10 py-2 px-4",
        xs: "h-8 px-3 rounded-md",
        sm: "h-9 px-3 rounded-md",
        lg: "h-11 px-8 rounded-md",
        // Harvey Norman Custom
        custom: "",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
